import React, { Fragment } from 'react';
import { action, computed, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';

import { ComponentBase } from '../../../shared/ComponentBase.js';
import { InjectedStyledComponent } from '../../../shared/InjectedStyledComponent.js';
import { IButtonProps } from '../Button/models/index.js';
import { IInputProps } from '../Input/models/index.js';
import SavedSearchList from '../SavedSearchList/SavedSearchList.js';
import { ITranslationContextValue } from '../Translate/models/index.js';
import { TranslationContext } from '../Translate/Translate.js';

import { ISavedSearchConfiguration, ISavedSearchProps, ISavedSearchState } from './models/index.js';
import ClickLayer from './styledComponents/ClickLayer.js';
import Input from './styledComponents/Input.js';
import ListButton from './styledComponents/ListButton.js';
import ListWrapper from './styledComponents/ListWrapper.js';
import SaveButton from './styledComponents/SaveButton.js';
import ToggleButton from './styledComponents/ToggleButton.js';
import Wrapper from './styledComponents/Wrapper.js';
import { SAVED_SEARCH_CATEGORY_KEY, SAVED_SEARCH_COMPONENT_KEY } from './SavedSearch.meta.js';

export class SavedSearch extends ComponentBase<ISavedSearchProps, ISavedSearchState, ISavedSearchConfiguration> {
	categoryKey = SAVED_SEARCH_CATEGORY_KEY;
	componentKey = SAVED_SEARCH_COMPONENT_KEY;

	showInput = false;
	showList = false;

	constructor(props: ISavedSearchProps) {
		super(props);

		makeObservable(this, {
			showInput: observable,
			showList: observable,
			saveButtonProps: computed,
			toggleButtonProps: computed,
			listButtonProps: computed,
			inputProps: computed,
			toggleSave: action,
			onSave: action,
			toggleList: action,
		});

		this.showInput = !!props.showInput;
	}

	get saveButtonProps(): IButtonProps {
		const { suppressAnalytics } = this.props;
		return {
			iconProps: {
				asset: 'base.savedSearch.save',
			},
			onClick: () => {
				this.onSave();
			},
			suppressAnalytics: suppressAnalytics == undefined ? true : suppressAnalytics,
			disabled: this.props.saveButtonDisabled,
		};
	}

	get toggleButtonProps(): IButtonProps {
		const asset = this.showInput ? 'base.savedSearch.toggleClosed' : 'base.savedSearch.toggleOpen';
		const { suppressAnalytics } = this.props;
		return {
			iconProps: {
				asset,
			},
			onClick: () => {
				this.toggleSave();
			},
			suppressAnalytics: suppressAnalytics == undefined ? true : suppressAnalytics,
		};
	}

	get listButtonProps(): IButtonProps {
		const { suppressAnalytics } = this.props;
		return {
			iconProps: {
				asset: 'base.savedSearch.list',
			},
			onClick: () => {
				this.toggleList();
			},
			suppressAnalytics: suppressAnalytics == undefined ? true : suppressAnalytics,
		};
	}

	get inputProps(): IInputProps {
		const { input } = this.props;
		return input || {};
	}

	toggleSave(mustShowInput?: boolean) {
		const { onToggle } = this.props;
		if (typeof mustShowInput !== 'boolean') {
			mustShowInput = !this.showInput;
		}
		if (onToggle) {
			onToggle(mustShowInput);
		}
		this.showInput = mustShowInput;
	}

	onSave() {
		const { onSave } = this.props;
		if (this.showInput && onSave) {
			onSave();
		}
	}

	toggleList(mustShowList?: boolean) {
		if (typeof mustShowList !== 'boolean') {
			mustShowList = !this.showList;
		}
		this.showList = mustShowList;
	}

	componentDidUpdate(previousProps: ISavedSearchProps) {
		const { showInput } = this.props;
		if (showInput !== undefined && previousProps.showInput !== showInput) {
			this.showInput = showInput;
		}
	}

	translateValue(translationContext: ITranslationContextValue, value?: string) {
		return value ? translationContext.translate(value) : value;
	}

	render() {
		const { className, savedSearchList } = this.props;
		return (
			<Wrapper className={className} key={`${this.showInput}=${this.showList}`}>
				<TranslationContext.Consumer>
					{(translationContext: ITranslationContextValue) => {
						const inputProps = this.inputProps;
						if (inputProps) {
							inputProps.placeholder = this.translateValue(translationContext, inputProps.placeholder);
						}
						return (
							<React.Fragment>
								{this.showInput && (
									<Fragment>
										<Input {...inputProps} />
										<SaveButton {...this.saveButtonProps} />
									</Fragment>
								)}
								<ToggleButton {...this.toggleButtonProps} />
								<ListButton {...this.listButtonProps} />
								{this.showList && (
									<Fragment>
										<ClickLayer
											onClick={() => {
												this.toggleList(false);
											}}
										/>
										<ListWrapper>
											<SavedSearchList {...savedSearchList} />
										</ListWrapper>
									</Fragment>
								)}
							</React.Fragment>
						);
					}}
				</TranslationContext.Consumer>
			</Wrapper>
		);
	}
}

export default InjectedStyledComponent(observer(SavedSearch), SAVED_SEARCH_CATEGORY_KEY, SAVED_SEARCH_COMPONENT_KEY);
